/// <reference path="../../../../typings/index.d.ts" />

module Meevo.Plugins.WalkinManager {
    import EmployeeGenderSearchEnum = Meevo.Model.EmployeeGenderSearchEnum;
    import CustomerNameFormat = Meevo.Model.CustomerNameDisplayFormatEnum;

    export enum IAppoinmentState {
        Active = 0,
        NoShow,
        Cancelled
    }

    export interface IWalkinAppointment extends Meevo.Model.IWalkinAppointmentBase {
        appointServiceId: string;
        appointmentId?: string;
        clientDisplayName: string,
        clientFirstName: string,
        clientMiddleName: string,
        clientLastName: string,
        clientId: Guid,
        clientPrimaryPhoneNumber: string,
        clientPrimaryCountryCode: string,
        dateCreated: string;
        employeeId: Guid;
        employeeNickName: string;
        employeeDisplayName: string;
        endDateTime: string;
        entityVersion: number;
        id: Guid;
        isRescanRequired: boolean;
        priorityOrder: number;
        serviceDisplayName: string;
        serviceId: Guid;
        startDateTime: string;
        isCheckedIn?: boolean;
        totalWaitTime?: number|string;
        estimatedWaitTime?: number|string;
        state: IAppoinmentState
    }

    export interface IWalkinAppointmentService {
        walkinAppointmentList: Array<IWalkinAppointment>;
        pagedWalkInList: Array<Array<IWalkinAppointment>>;
        activePage: number;
        walkinStats: IWalkinStats;
        updateTime: Function;
        fetchWalkInAppointments: Function;

        updateAllAppointmentServiceByClientId<T>(clientId: string, data: T): ng.IPromise<any>;
        updateOneAppointmentServiceByAppointmentId<T>(appointmentId: Guid, data: T): ng.IPromise<any>;
        getAppointment(appointmentId: Guid): ng.IPromise<IWalkinAppointment>;
    }

    export interface IWalkinStats {
        averageWaitTime: number;
        totalWalkIns: number;
        totalServed: number;
        totalServices: number;
        totalNoShows: number;
    }

    export interface IWalkinAppointmentResponse {
        items: Array<IWalkinAppointment>;
        stats: IWalkinStats;
    }

    export class WalkinAppointmentService implements IWalkinAppointmentService{
        private _walkinAppointmentList: Array<IWalkinAppointment>;
        private _pagedWalkInList: Array<Array<IWalkinAppointment>>;
        private _walkinStats: IWalkinStats;
        private _activePage: number;

        constructor (
            protected appSettings: Meevo.Components.Common.IAppSettings,
            protected dataService: Meevo.Components.BaseClasses.IDataService,
            protected walkInSettingsService: Meevo.Plugins.WalkinManager.IWalkInSettingsService
        ) {
            this._walkinAppointmentList = [];
            this._pagedWalkInList = [];
            this._activePage;
        }

        public fetchWalkInAppointments(viewState: Meevo.Model.WaitListStatus,
            sortType: Meevo.Model.WaitListSortType = Meevo.Model.WaitListSortType.Priority): angular.IPromise<any> {

            return this.dataService.getListPaged(this.appSettings.API_ENDPOINTS.walkinAppointment, {
                itemsPerPage: 999,
                sortBy: (sortType === Meevo.Model.WaitListSortType.Priority ? 'PriorityOrder' : 'ClientLastName,ClientFirstName'),
                criteria: {
                    'walkInStatus': null,
                    'isRescanRequired': null,
                    'view': viewState
                }
            })
                .then((data: IWalkinAppointmentResponse | any) => {
                    this.walkinAppointmentList = data.items;
                    this.walkinStats = data.stats;

                    let items = data.items;

                    if (viewState === Meevo.Model.WaitListStatus.InProgress) {
                        items = data.items.filter(item =>
                            (
                                item.appointmentServiceTransitionState === Meevo.Model.AppointStateEnum.CheckedIn &&
                                item.appointmentServiceFinalState === Meevo.Model.AppointFinalStateEnum.None
                            ) ||
                            item.appointmentServiceFinalState === Meevo.Model.AppointFinalStateEnum.CheckedOut
                        );
                        
                        this.walkinAppointmentList = items;
                    }

                    this._pagedWalkInList = this.formatPagedList(items, 10);
                    this.updateActivePage();
                })
        }

        get walkinAppointmentList(): Array<IWalkinAppointment> {
            return this._walkinAppointmentList;
        }

        set walkinAppointmentList(data: Array<IWalkinAppointment>) {
            this._walkinAppointmentList = this.formatAppointmentList(data);
        }

        get walkinStats(): IWalkinStats {
            return this._walkinStats;
        }

        set walkinStats(data: IWalkinStats) {
            this._walkinStats = WalkinAppointmentService.formatStats(data);
        }

        get pagedWalkInList(): Array<Array<IWalkinAppointment>> {
            return this._pagedWalkInList;
        }

        set pagedWalkInList(data: Array<Array<IWalkinAppointment>>) {
            this._pagedWalkInList = data;
        }

        get activePage(): number {
            return this._activePage;
        }
   
        formatAppointmentList (data: Array<IWalkinAppointment>): Array<IWalkinAppointment> {
            let list = data;

            list.forEach((item: IWalkinAppointment): void => {
                WalkinAppointmentService.setCheckInStatus(item);
                WalkinAppointmentService.setTotalWaitTime(item);
                WalkinAppointmentService.setEstimatedWaitTime(item);
                WalkinAppointmentService.setEmployeePreferenceName(item);
                this.setParsedDisplayName(item);
            });

            return list;
        }

        formatPagedList(data: Array<IWalkinAppointment>, pageSize: number): Array<Array<IWalkinAppointment>> {
            var pagedArray = [];
            for (var i = 0; i < data.length; i += pageSize) {
                var page = data.slice(i, i + pageSize);
                pagedArray.push(page);
            }
            return pagedArray;
        }

        updateActivePage(): void {
            if (this._activePage >= 0 && this._activePage < this._pagedWalkInList.length - 1) {
                this._activePage += 1;
            } else {
                this._activePage = 0;
            }
        }

        public updateAllAppointmentServiceByClientId<T>(clientId: string, data: T): ng.IPromise<any> {
            return this.dataService.patch(`${this.appSettings.API_ENDPOINTS.walkinAppointment}/client/${clientId}/appointmentservice`, data)
        }

        public updateOneAppointmentServiceByAppointmentId<T>(appointmentId: Guid, data: T): ng.IPromise<any> {
            return this.dataService.patch(`${this.appSettings.API_ENDPOINTS.walkinAppointment}`, data, appointmentId)
        }

        public getAppointment(appointmentId: Guid): ng.IPromise<IWalkinAppointment> {
            return this.dataService.getItem(`${this.appSettings.API_ENDPOINTS.walkinAppointment}`, appointmentId);
        }

        static formatStats (data: IWalkinStats): IWalkinStats {
            data.averageWaitTime = Math.round(data.averageWaitTime / 60);

            return data;
        }


        public updateTime (): void {
            this._walkinAppointmentList.forEach((item: IWalkinAppointment): void => {
                WalkinAppointmentService.setTotalWaitTime(item);
                WalkinAppointmentService.setEstimatedWaitTime(item);
            })
        }

        static setCheckInStatus (item: IWalkinAppointment): void {
            item['isCheckedIn'] = Boolean(item.statusEnum === Meevo.Model.WalkInStatusEnum.WaitingAndPresent);
        }

        static setTotalWaitTime (item: IWalkinAppointment): void {
            item['totalWaitTime'] = Math.floor(Number(item.totalWaitTime)).toString();
        }

        static setEstimatedWaitTime (item: IWalkinAppointment): void {
            let time = Math.floor(Number(item.estimatedWaitTime));
            item['estimatedWaitTime'] = time > 0 ? time : 'Next';

            if (item.appointmentServiceTransitionState === Meevo.Model.AppointStateEnum.Conflict) {
                item['estimatedWaitTime'] = 'Conflict';
            }
        }

        static setEmployeePreferenceName (item: IWalkinAppointment): void {
            switch (item['employeeGenderSearchEnum']) {
                case EmployeeGenderSearchEnum.AnyMale:
                    item['parsedEmployeePreference'] = 'First Available Male';
                    break;
                case EmployeeGenderSearchEnum.AnyFemale:
                    item['parsedEmployeePreference'] = 'First Available Female';
                    break;
                case EmployeeGenderSearchEnum.AnyEmployee:
                    item['parsedEmployeePreference'] = 'First Available';
                    break;
                default:
                    item['parsedEmployeePreference'] = 'Specific Service Provider';
            }
        }

        private setParsedDisplayName (item: IWalkinAppointment): void {
            let firstInitial = '',
                middleInitial = '',
                lastInitial = '',
                firstName = '',
                middleName = '',
                lastName = '',
                lastPhone = '';

            if (typeof item.clientFirstName === 'undefined') {
                firstName = '';
                firstInitial = firstName;
            } else {
                firstName = item.clientFirstName;
                firstInitial = firstName[0];
            }

            if (typeof item.clientLastName === 'undefined') {
                lastName = '';
                lastInitial = lastName;
            } else {
                lastName = item.clientLastName;
                lastInitial = lastName[0];
            }

            if (typeof item.clientMiddleName === 'undefined') {
                middleName = '';
                middleInitial = middleName;
            } else {
                middleName = item.clientMiddleName;
                middleInitial = middleName[0] + ".";
            }

            if (typeof item.clientPrimaryPhoneNumber === 'undefined') {
                lastPhone = '';
            } else {
                lastPhone = item.clientPrimaryPhoneNumber.substr(item.clientPrimaryPhoneNumber.length - 4, 4);
            }

            switch (this.walkInSettingsService.settings.customerNameFormat) {
                case CustomerNameFormat.FirstNameCommaLast4ofPhoneNumber:
                    item['parsedClientDisplayName'] = `${firstName}, ${lastPhone}`; // John, 6508 (First name, last 4 of phone number)
                    break;
                case CustomerNameFormat.FirstInitialCommaLastInitial:
                    item['parsedClientDisplayName'] = `${firstInitial}. ${middleInitial} ${lastInitial}.`; // J. H. (First initial, Last initial)
                    break;
                case CustomerNameFormat.FirstNameCommaLastInitial:
                    item['parsedClientDisplayName'] = `${firstName} ${lastInitial}.`; // John H. (First name, Last initial)
                    break;
                case CustomerNameFormat.FirstInitialCommaLastName:
                    item['parsedClientDisplayName'] = `${firstInitial}, ${lastName}`; // J. Harms (First initial, Last name)
                    break;
            }
        }

        static factory(): Function {
            let instance = (
                appSettings: Meevo.Components.Common.IAppSettings,
                dataService: Meevo.Components.BaseClasses.IDataService,
                walkInSettingsService: Meevo.Plugins.WalkinManager.IWalkInSettingsService
            ) => {
                return new WalkinAppointmentService(appSettings, dataService, walkInSettingsService);
            };

            instance.$inject = ['meevo.AppSettings', 'meevo.components.common.DataService',
                'meevo.plugins.walkinManager.WalkInSettingsService'];
            return instance;
        }
    }
    angular.module("meevo.plugins.walkinManager")
        .service('meevo.plugins.walkinManager.WalkinAppointmentService', WalkinAppointmentService.factory());

}
